// AdminLayout — sidebar fija desktop-first para panel admin.
// Clona el patrón visual de ClienteLayout pero adaptado a la densidad del admin
// (más items, grupos colapsables). Mobile: drawer overlay.

import { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { useAdminPresence, pathToLabel } from '../lib/useAdminPresence'
import { useSystemHealth } from '../lib/useSystemHealth'
// 2026-06-10: badge "👁️ lectura" en sidebar para admin (no superadmin).
// El backend bloquea writes con RLS — el badge solo le recuerda al admin su rol.

// Reorganizado 2026-06-09: -5 items top-level, CRM promovido a nivel 1 (uso diario),
// Compras movido a Catálogo (cerca de OC + proveedores),
// Documentación + Infraestructura agrupadas en "Sistema". Finanzas: 5 subgrupos → 3.
// Reorganizado 2026-07-26: "Web & Marketing" se fusionó dentro de "Comunicación"
// (una sola sección, flujo numerado). Manejo de la web quedó como link suelto.
const MENU = [
  // Reorg 2026-09-02 (estilo mockup): menu por FUNCION, no por canal.
  // Diario arriba (plano), luego GESTION (Finanzas/Reportes) y SISTEMA (Configuracion).
  // Kits -> Stock, Porteros -> Clientes, Hub KPIs -> Inicio (fuera del menu),
  // Sitio web -> Configuracion, ML operativo agrupado bajo "Ventas".
  // Nada se borro: todas las paginas movidas siguen ruteadas y accesibles (Cmd+K).
  { type: 'link',  to: '/admin',                end: true,  label: 'Inicio',          icon: '📊' },

  { type: 'link',  to: '/admin/ventas', end: true, label: 'Ventas', icon: '🛒', badgeKey: 'ml_ventas_nuevas' },

  { type: 'group', label: 'WhatsApp',           icon: '💬', items: [
    { to: '/admin/crm',                        label: 'Bandeja',              icon: '💬', badgeKey: 'wa_no_leidos' },
    { to: '/admin/crm/contactos',              label: 'Contactos',            icon: '📒' },
  ]},

  { type: 'group', label: 'Clientes',           icon: '👥', items: [
    { to: '/admin/usuarios?tipo=b2c',          label: 'Clientes (B2C)',       icon: '👤' },
    { to: '/admin/clientes/suscriptores',      label: 'Suscriptores',         icon: '🎁' },
    { to: '/admin/ventas-manuales',            label: 'Ventas manuales',      icon: '🛒' },
    { to: '/admin/empresas',                   label: 'Empresas (B2B)',       icon: '🏢' },
    { to: '/admin/pedidos-b2b',                label: 'Pedidos B2B',          icon: '💼' },
    { to: '/admin/pedidos',                    label: 'Control de pedidos',   icon: '🛍️', badgeKey: 'pedidos_pendientes' },
    { to: '/admin/consultas-comerciales',      label: 'Consultas web',        icon: '📥', badgeKey: 'consultas_nuevas' },
    // 2026-09-09: Porteros pausado por ahora — foco en vendedores. Rutas siguen vivas.
    // { to: '/admin/porteros',                   label: 'Porteros',               icon: '🛎️' },
    // { to: '/admin/postulaciones-porteros',     label: 'Postulaciones porteros', icon: '📥' },
    { to: '/admin/vendedores',                 label: 'Aliados',                icon: '💼' },
  ]},

  { type: 'group', label: 'Logística',          icon: '🚚', items: [
    { to: '/admin/entregas',                   label: 'Entregas',             icon: '📦' },
    { to: '/repartidor/hoy',                   label: 'Mi ruta',              icon: '📱' },
    { to: '/admin/repartidores',               label: 'Repartidores',         icon: '🚐' },
    { to: '/admin/barrios',                    label: 'Barrios',              icon: '🗺️' },
    { to: '/admin/recordatorios',              label: 'Recordatorios WA',     icon: '📲' },
    { to: '/admin/logistica/limpackflex',      label: 'LimpackFlex',          icon: '⚡' },
    { to: '/admin/logistica/despachos',        label: 'Despachos interior',   icon: '🚚' },
  ]},

  { type: 'group', label: 'Stock',              icon: '📦', items: [
    { to: '/admin/stock',                      label: 'Stock productos',      icon: '📊' },
    { to: '/admin/kits',                       label: 'Kits',                 icon: '🎁' },
    { to: '/admin/auditoria-kits',             label: 'Auditoría de kits',    icon: '🔍' },
    { to: '/admin/proveedores',                label: 'Proveedores',          icon: '🏭' },
    { to: '/admin/orden-compra',               label: 'Órdenes de compra',    icon: '📋' },
    { to: '/admin/materiales',                 label: 'Stock materiales',     icon: '🧰' },
  ]},

  { type: 'group', label: 'Comunicación',       icon: '📣', items: [
    { to: '/admin/comunicacion',               label: 'Panel',                icon: '🧭', end: true },
    { to: '/admin/comunicacion/plan',          label: '1 · Plan semanal',     icon: '🗓️' },
    { to: '/admin/comunicacion/piezas',        label: '2 · Publicar',         icon: '📱' },
    { to: '/admin/comunicacion/pauta',         label: '3 · Pauta catálogo',   icon: '🎯' },
    { to: '/admin/comunicacion/folleteria',    label: 'Folletería',           icon: '📄' },
    { to: '/admin/marketing/audiencias',       label: 'Resultados',           icon: '📊' },
  ]},

  // ═══════════════════════ PETPACK (canal mascotas · solo superadmin) ═══════════════════════
  { type: 'section', label: 'PETPACK',           icon: '🐾', accent: 'shared', soloSuper: true },
  { type: 'link', to: '/admin/petpack',          label: 'PetPack', icon: '🐾', soloSuper: true },
  { type: 'link', to: '/admin/petpack/precios',  label: 'PetPack · Precios ML', icon: '🏷️', soloSuper: true },

  // ═══════════════════════ CANALES ═══════════════════════
  { type: 'section', label: 'CANALES',          icon: '🔗', accent: 'ml' },
  { type: 'link', to: '/admin/canales/mercadolibre/preguntas', label: 'Preguntas ML', icon: '❓', badgeKey: 'ml_preguntas_sin_responder' },
  { type: 'link', to: '/admin/canales/mercadolibre/despacho',  label: 'Despacho ML',  icon: '📦', badgeKey: 'ml_pendientes_despacho' },
  { type: 'link', to: '/admin/canales/mercadolibre/reclamos',  label: 'Reclamos ML',  icon: '🚨', badgeKey: 'ml_reclamos_abiertos' },
  { type: 'link', to: '/admin/inteligencia-mercado',           label: 'Inteligencia de mercado', icon: '📊' },
  { type: 'link', to: '/admin/analisis-catalogo',              label: 'Análisis catálogos',      icon: '🔍' },

  // ═══════════════════════ GESTIÓN ═══════════════════════
  { type: 'section', label: 'GESTIÓN',          icon: '📈', accent: 'shared' },
  { type: 'group', label: 'Finanzas', icon: '💰', subgroups: [
    { label: 'Operación',               icon: '💳', items: [
      { to: '/admin/cobros-pendientes',           label: 'Transferencias pend.',  icon: '🏦' },
      { to: '/admin/cobros-mercadopago',          label: 'Cobros MercadoPago',   icon: '🔷' },
      { to: '/admin/cobros',                      label: 'Cobros B2C (gestión)', icon: '💰' },
      { to: '/admin/cobros-b2b',                  label: 'Cobros B2B (gestión)', icon: '🏦' },
      { to: '/admin/facturacion',                 label: 'Facturación emitida',  icon: '📤' },
      { to: '/admin/finanzas/comisiones-handy',   label: 'Comisiones Handy',     icon: '💳' },
      { to: '/admin/canales/mercadolibre/comisiones', label: 'Comisiones ML',    icon: '🤝' },
      { to: '/admin/finanzas/conciliacion',       label: 'Conciliación',         icon: '🏦' },
      { to: '/admin/finanzas/conciliar',          label: 'Conciliar banco',     icon: '🔄' },
      { to: '/admin/compras',                     label: 'Facturas recibidas',   icon: '🧾' },
      { to: '/admin/finanzas/pagos-proveedores',  label: 'Pagos proveedores',    icon: '💸' },
      { to: '/admin/finanzas/sueldos',            label: 'Sueldos',              icon: '👔' },
      { to: '/admin/configuracion-bancaria',      label: 'Datos bancarios',      icon: '🏦' },
    ]},
    { label: 'Contabilidad',            icon: '📒', items: [
      { to: '/admin/finanzas/reportes',           label: 'Reportes contables',   icon: '📊' },
      { to: '/admin/finanzas/asientos',           label: 'Asientos',             icon: '📝' },
      { to: '/admin/finanzas/plan-cuentas',       label: 'Plan de cuentas',      icon: '📚' },
      { to: '/admin/finanzas/gastos-fijos',       label: 'Gastos fijos',         icon: '💸' },
      { to: '/admin/finanzas/centros-costo',      label: 'Centros de costo',     icon: '🎯' },
      { to: '/admin/finanzas/activos-fijos',      label: 'Activos fijos',        icon: '🏷️' },
    ]},
    { label: 'Moneda extranjera',       icon: '💱', items: [
      { to: '/admin/finanzas/tipos-cambio',       label: 'Tipos de cambio BCU',  icon: '💵' },
      { to: '/admin/finanzas/multimoneda',        label: 'Multimoneda USD',      icon: '💱' },
    ]},
  ]},

  { type: 'group', label: 'Reportes',           icon: '📈', items: [
    { to: '/admin/reportes',                   label: 'General (P&L)',         icon: '📊' },
    { to: '/admin/reportes/mrr-churn',         label: 'MRR + Churn',           icon: '📈' },
    { to: '/admin/reportes/cohort-ltv',        label: 'Cohort + LTV',          icon: '👥' },
    { to: '/admin/reportes/rentabilidad-producto', label: 'Rent. por producto', icon: '🏆' },
    { to: '/admin/reportes/rentabilidad-barrio',   label: 'Rent. por barrio',   icon: '🗺️' },
    { to: '/admin/reportes/vecinos',           label: 'Vecinos / Referidos',   icon: '🏢' },
    { to: '/admin/reportes/stock-turnover',    label: 'Stock turnover',        icon: '🔄' },
    { to: '/admin/reportes/valuacion-fifo',    label: 'Valuación FIFO',        icon: '📦' },
    { to: '/admin/reportes/modelo-financiero', label: 'Modelo financiero',     icon: '📐' },
  ]},

  // ═══════════════════════ SISTEMA ═══════════════════════
  { type: 'section', label: 'SISTEMA',          icon: '⚙️', accent: 'shared' },
  { type: 'group', label: 'Configuración',      icon: '⚙️', subgroups: [
    { label: 'Sitio & catálogo',        icon: '🎨', items: [
      { to: '/admin/tienda',                     label: 'Sitio web',            icon: '🎨' },
      { to: '/admin/logistica/medidas-envio',    label: 'Medidas envío',        icon: '📏' },
    ]},
    { label: 'MercadoLibre',            icon: '🤝', items: [
      { to: '/admin/canales/mercadolibre/configuracion',    label: 'Conexión ML',            icon: '🔑' },
      { to: '/admin/canales/mercadolibre/tarifas',          label: 'Tarifas ML',             icon: '💰' },
      { to: '/admin/canales/mercadolibre/fotos-fijas',      label: 'Fotos fijas',            icon: '🖼' },
      { to: '/admin/canales/mercadolibre/descripcion-fija', label: 'Texto fijo descripción', icon: '📝' },
      { to: '/admin/canales/mercadolibre/eventos',          label: 'Log de webhooks',        icon: '📡' },
    ]},
    { label: 'WhatsApp / CRM',          icon: '💬', items: [
      { to: '/admin/crm/templates',              label: 'Templates WA',         icon: '📝' },
      { to: '/admin/crm/configuracion',          label: 'Config WhatsApp',      icon: '⚙️' },
      { to: '/admin/integraciones',              label: 'Integraciones CRM',    icon: '🔌' },
    ]},
    { label: 'Equipo',                  icon: '👤', items: [
      { to: '/admin/usuarios',                   label: 'Usuarios',             icon: '👤' },
    ]},
    { label: 'Sistema',                 icon: '🛠', items: [
      { to: '/admin/docs',                       label: 'Documentación',        icon: '📚' },
      { to: '/admin/infraestructura',            label: 'Infraestructura',      icon: '⚡', statusDot: true },
      { to: '/admin/monitor-externo',            label: 'Monitor Supabase + CF', icon: '📡' },
      { to: '/admin/configuracion-ia',           label: 'Configuración IA',     icon: '🤖' },
    ]},
  ]},
]

const AL_ICONS = {
  home: (<path d="M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/>),
  chat: (<path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/>),
  grid: (<path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"/>),
  bars: (<path d="M5 20v-7M11 20V8M17 20v-4M3 20h18"/>),
  users: (<path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM21 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8"/>),
  door: (<path d="M4 21h16M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M14 12h1"/>),
  map: (<path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4zM8 2v16M16 6v16"/>),
  box: (<path d="M21 8l-9-5-9 5v8l9 5 9-5zM3 8l9 5 9-5M12 13v8"/>),
  mega: (<path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1zM15 9a4 4 0 0 1 0 6M18 6a8 8 0 0 1 0 12"/>),
  globe: (<><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></>),
  bag: (<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0"/>),
  trend: (<path d="M22 7l-8.5 8.5-5-5L2 17M16 7h6v6"/>),
  help: (<><circle cx="12" cy="12" r="9"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r=".9" fill="currentColor" stroke="none"/></>),
  truck: (<><path d="M1 5h13v11H1zM14 9h4l4 4v3h-8z"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></>),
  alert: (<><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4"/><circle cx="12" cy="17" r=".9" fill="currentColor" stroke="none"/></>),
  stock: (<path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4"/>),
  dollar: (<path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>),
  file: (<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8"/>),
  gear: (<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>),
  eye: (<><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></>),
  store: (<path d="M3 9l1.5-5h15L21 9M3 9v11h18V9M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M9 20v-6h6v6"/>),
}
const AL_ICON_BY_LABEL = { 'Inicio':'home','WhatsApp':'chat','Hub · KPIs':'grid','Clientes':'users','Porteros':'door','Logística':'map','Kits':'box','Comunicación':'mega','Sitio web':'globe','Ventas':'bag','Inteligencia de mercado':'trend','Preguntas':'help','Despacho':'truck','Reclamos':'alert','Preguntas ML':'help','Despacho ML':'truck','Reclamos ML':'alert','Stock & Compras':'stock','Stock':'stock','Finanzas':'dollar','Reportes':'file','Configuración':'gear' }
function iconFor(label){ return AL_ICON_BY_LABEL[label] || 'grid' }
function AlIco({ id }){ return (<svg className="al-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{AL_ICONS[id] || AL_ICONS.grid}</svg>) }

export default function AdminLayout({ children, title }) {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const path = location.pathname
  // Refresh visual moderno (Fase 1): se aplica a todo el admin EXCEPTO Finanzas y Reportes,
  // que Nico decidio dejar con el look actual. Si una ruta cae fuera de la lista legacy,
  // recibe el look moderno (fail-safe: nunca rompe, solo estetica).
  const AL_LEGACY_PREFIXES = [
    '/admin/finanzas', '/admin/reportes', '/admin/facturacion',
    '/admin/cobros', '/admin/compras', '/admin/configuracion-bancaria',
    '/admin/canales/mercadolibre/comisiones',
  ]
  const alUiModern = !AL_LEGACY_PREFIXES.some(pre => path === pre || path.startsWith(pre + '/') || path.startsWith(pre))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [badges, setBadges] = useState({})

  // Command palette (Cmd+K / Ctrl+K)
  const [paletteOpen, setPaletteOpen] = useState(false)
  useEffect(() => {
    function onKey(e) {
      const meta = e.metaKey || e.ctrlKey
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setPaletteOpen(v => !v)
      } else if (e.key === 'Escape' && paletteOpen) {
        setPaletteOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [paletteOpen])

  // Conteos para badges en el menú (consultas nuevas, mensajes WA no leídos, etc.)
  useEffect(() => {
    let cancelado = false
    async function fetchBadges() {
      try {
        // Consultas comerciales nuevas
        const { count: consultasCount } = await supabase
          .from('consultas_comerciales')
          .select('id', { count: 'exact', head: true })
          .eq('estado', 'nuevo')

        // Mensajes WA no leídos (suma de unread_count en conversaciones abiertas)
        const { data: convs } = await supabase
          .from('conversations')
          .select('unread_count')
          .gt('unread_count', 0)
          .neq('estado', 'cerrada')
        const waCount = (convs || []).reduce((s, c) => s + (c.unread_count || 0), 0)

        // Preguntas ML sin responder
        const { count: preguntasCount } = await supabase
          .from('ml_preguntas')
          .select('id', { count: 'exact', head: true })
          .or('status.eq.UNANSWERED,answer_text.is.null')

        // Ventas ML pendientes de despacho (paid + sin date_shipped)
        const { count: despachoCount } = await supabase
          .from('ml_ventas')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'paid')
          .is('date_shipped', null)

        // Ventas ML nuevas de las últimas 24hs (para saber si hay que revisar el panel)
        const hace24 = new Date(Date.now() - 24 * 3600 * 1000).toISOString()
        const { count: ventasMlNuevas } = await supabase
          .from('ml_ventas')
          .select('id', { count: 'exact', head: true })
          .gte('date_created', hace24)

        // Ventas Limpack (pedidos one-shot) pendientes de gestión (pagado + en_armado)
        const { count: pedidosPendientes } = await supabase
          .from('pedidos')
          .select('id', { count: 'exact', head: true })
          .in('estado', ['pagado', 'en_armado'])

        // Reclamos ML abiertos (status != closed)
        const { count: reclamosAbiertos } = await supabase
          .from('ml_reclamos')
          .select('id', { count: 'exact', head: true })
          .neq('status', 'closed')

        if (!cancelado) setBadges(b => ({
          ...b,
          consultas_nuevas: consultasCount || 0,
          wa_no_leidos: waCount,
          ml_preguntas_sin_responder: preguntasCount || 0,
          ml_pendientes_despacho: despachoCount || 0,
          ml_ventas_nuevas: ventasMlNuevas || 0,
          ml_reclamos_abiertos: reclamosAbiertos || 0,
          pedidos_pendientes: pedidosPendientes || 0,
        }))
      } catch (_) { /* silent */ }
    }
    fetchBadges()
    const t = setInterval(fetchBadges, 30000) // refresh cada 30s para que WA sea casi-realtime
    return () => { cancelado = true; clearInterval(t) }
  }, [path])
  // Helper: ¿el grupo (o cualquiera de sus sub-grupos) contiene el path activo?
  function groupHasPath(g, p) {
    if (g.items) return g.items.some(i => p.startsWith(i.to))
    if (g.subgroups) return g.subgroups.some(sg => sg.items.some(i => p.startsWith(i.to)))
    return false
  }

  // Recordar grupos abiertos en localStorage (incluye sub-grupos via clave "Padre/Sub")
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_open_groups_v2')
      if (saved) return JSON.parse(saved)
    } catch (_) {}
    // Default: abre el grupo y sub-grupo que contienen la página activa
    const init = {}
    MENU.forEach(g => {
      if (g.type !== 'group') return
      if (groupHasPath(g, path)) {
        init[g.label] = true
        if (g.subgroups) {
          g.subgroups.forEach(sg => {
            if (sg.items.some(i => path.startsWith(i.to))) init[`${g.label}/${sg.label}`] = true
          })
        }
      }
    })
    return init
  })

  useEffect(() => {
    // Auto-abrir el grupo y sub-grupo al navegar a una sección dentro
    setOpenGroups(prev => {
      const next = { ...prev }
      MENU.forEach(g => {
        if (g.type !== 'group') return
        if (!groupHasPath(g, path)) return
        next[g.label] = true
        if (g.subgroups) {
          g.subgroups.forEach(sg => {
            if (sg.items.some(i => path.startsWith(i.to))) next[`${g.label}/${sg.label}`] = true
          })
        }
      })
      return next
    })
    setDrawerOpen(false)
  }, [path])

  useEffect(() => {
    try { localStorage.setItem('admin_open_groups_v2', JSON.stringify(openGroups)) } catch (_) {}
  }, [openGroups])

  function toggleGroup(label) {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }))
  }

  // Preservar scrollTop del sidebar entre navegaciones — sin esto, cada click
  // hace que la barra lateral salte al tope.
  const sideRef = useRef(null)
  useEffect(() => {
    const el = sideRef.current
    if (!el) return
    try {
      const saved = sessionStorage.getItem('admin_side_scroll')
      if (saved) el.scrollTop = parseInt(saved, 10) || 0
    } catch (_) {}
    function onScroll() {
      try { sessionStorage.setItem('admin_side_scroll', String(el.scrollTop)) } catch (_) {}
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  async function handleLogout() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  function isLinkActive(to, end) {
    if (end) return path === to
    return path === to || path.startsWith(to + '/')
  }

  function isGroupActive(items) {
    return (items || []).some(i => isLinkActive(i.to, false))
  }
  function isSuperGroupActive(g) {
    if (g.items) return isGroupActive(g.items)
    if (g.subgroups) return g.subgroups.some(sg => isGroupActive(sg.items))
    return false
  }

  const initial = (profile?.nombre || user?.email || 'U').charAt(0).toUpperCase()
  const displayName = profile?.nombre?.split(' ')[0] || (user?.email || '').split('@')[0]
  const fullEmail = user?.email || ''

  // Presencia: lista de admins online (incluye self)
  const adminsOnline = useAdminPresence()
  const otrosAdmins = adminsOnline.filter(a => a.user_id !== user?.id)

  // Estado del sistema (verde/amarillo/rojo) — se muestra como dot al lado del item Infraestructura
  const { resumen: healthResumen } = useSystemHealth()
  const healthDotColor = healthResumen.estadoGlobal === 'error' ? '#DC2626'
                       : healthResumen.estadoGlobal === 'warn'  ? '#F59E0B'
                       : '#22C55E'
  const healthLabel = healthResumen.estadoGlobal === 'error' ? 'Algo está caído'
                    : healthResumen.estadoGlobal === 'warn'  ? 'Hay servicios con warnings'
                    : 'Todo OK'

  // Alerta en title de la pestaña si algo está rojo (antes lo hacía SystemStatusSidebar)
  useEffect(() => {
    const titleOriginal = document.title.replace(/^⚠ /, '').replace(/^🟡 /, '')
    if (healthResumen.estadoGlobal === 'error') document.title = '⚠ ' + titleOriginal
    else if (healthResumen.estadoGlobal === 'warn') document.title = '🟡 ' + titleOriginal
    else document.title = titleOriginal
  }, [healthResumen.estadoGlobal])

  function NavBody() {
    return (
      <>
        <Link to="/admin" className="al-logo"><span className="al-logo-mark">L</span><span className="al-logo-tx"><span className="al-logo-name">Limpack</span><span className="al-logo-sub">Panel admin</span></span></Link>

        <RelojMontevideo />

        {/* Trigger del command palette */}
        <button className="al-cmd-trigger" onClick={() => setPaletteOpen(true)} title="Buscar (Cmd+K)">
          <span>🔍</span>
          <span style={{ flex: 1, textAlign: 'left' }}>Buscar pantalla…</span>
          <kbd>⌘K</kbd>
        </button>

        {/* Indicador "conectado como" + badge modo lectura para admin */}
        <div className="al-me">
          <span className="al-me-dot"></span>
          <span className="al-me-text">Conectado como <strong>{displayName}</strong></span>
          {profile?.role === 'admin' && (
            <span className="al-me-readonly" title="Como admin podés ver el panel pero solo superadmin puede modificar configuración crítica.">
              👁️ lectura
            </span>
          )}
        </div>

        {/* Otros admins online y donde están */}
        {otrosAdmins.length > 0 && (
          <div className="al-presence">
            <div className="al-presence-tag">Otros admins online</div>
            {otrosAdmins.map(a => (
              <div key={a.user_id} className="al-presence-row" title={a.email}>
                <span className="al-presence-dot"></span>
                <span className="al-presence-name">{a.nombre}</span>
                <span className="al-presence-where">{pathToLabel(a.path, MENU)}</span>
              </div>
            ))}
          </div>
        )}
        {/* "En vivo" y "Estado del sistema" se movieron a /admin/infraestructura
            (el dot al lado de "Infraestructura" en el menú indica el estado global) */}

        <nav className="al-nav">
          {(profile?.role === 'superadmin' ? MENU : MENU.filter((m) => !m.soloSuper)).map((m, idx) => {
            if (m.type === 'section') {
              return (
                <div key={`section-${idx}-${m.label}`} className={`al-menu-section-header al-section-${m.accent || 'shared'}`} aria-hidden>
                  <span className="al-menu-section-icon">{m.icon}</span>
                  <span className="al-menu-section-text">{m.label}</span>
                </div>
              )
            }
            if (m.type === 'link') {
              const active = isLinkActive(m.to, m.end)
              return (
                <Link key={m.to} to={m.to} className={`al-link ${active ? 'active' : ''}`}>
                  <AlIco id={iconFor(m.label)} />
                  <span>{m.label}</span>
                  {m.statusDot && (
                    <span
                      className={`al-status-pip al-status-pip-${healthResumen.estadoGlobal}`}
                      style={{ background: healthDotColor }}
                      title={`Estado del sistema · ${healthLabel}`}
                      aria-label={`Estado del sistema: ${healthLabel}`}
                    />
                  )}
                </Link>
              )
            }
            const open = !!openGroups[m.label]
            const active = isSuperGroupActive(m)
            const renderItem = (i) => {
              // i.end: items "padre" (ej. Panel de Comunicación) solo se marcan
              // activos en match exacto, no en todas las sub-rutas.
              const a = isLinkActive(i.to, !!i.end)
              const badgeCount = i.badgeKey ? badges[i.badgeKey] : 0
              return (
                <Link key={i.to} to={i.to} className={`al-sublink ${a ? 'active' : ''}`}>
                  <span className="al-sub-icon">{i.icon}</span>
                  <span>{i.label}</span>
                  {badgeCount > 0 && <span className="al-badge">{badgeCount}</span>}
                  {i.statusDot && (
                    <span
                      className={`al-status-pip al-status-pip-${healthResumen.estadoGlobal}`}
                      style={{ background: healthDotColor }}
                      title={`Estado del sistema · ${healthLabel}`}
                      aria-label={`Estado del sistema: ${healthLabel}`}
                    />
                  )}
                </Link>
              )
            }
            // Sumar badges de sub-items para mostrar en el header del grupo
            const sumarBadges = (items) => (items || []).reduce(
              (s, i) => s + (i.badgeKey ? (badges[i.badgeKey] || 0) : 0), 0
            )
            const groupBadge = sumarBadges(m.items) +
              (m.subgroups || []).reduce((s, sg) => s + sumarBadges(sg.items), 0)
            // ¿Algún sub-item tiene statusDot?
            const tieneStatusDot = (m.items || []).some(i => i.statusDot) ||
              (m.subgroups || []).some(sg => sg.items.some(i => i.statusDot))
            return (
              <div key={m.label} className={`al-group ${open ? 'open' : ''} ${active ? 'has-active' : ''}`}>
                <button type="button" className="al-group-head" onClick={() => toggleGroup(m.label)}>
                  <AlIco id={iconFor(m.label)} />
                  <span className="al-group-label">{m.label}</span>
                  {groupBadge > 0 && <span className="al-badge al-badge-group">{groupBadge}</span>}
                  {tieneStatusDot && (
                    <span
                      className={`al-status-pip al-status-pip-${healthResumen.estadoGlobal}`}
                      style={{ background: healthDotColor }}
                      title={`Estado del sistema · ${healthLabel}`}
                      aria-label={`Estado del sistema: ${healthLabel}`}
                    />
                  )}
                  <span className="al-chev">{open ? '▾' : '▸'}</span>
                </button>
                {open && (
                  <div className="al-group-items">
                    {m.items && m.items.map(renderItem)}
                    {m.subgroups && m.subgroups.map(sg => {
                      const sgKey = `${m.label}/${sg.label}`
                      const sgOpen = !!openGroups[sgKey]
                      const sgActive = isGroupActive(sg.items)
                      return (
                        <div key={sgKey} className={`al-subgroup ${sgOpen ? 'open' : ''} ${sgActive ? 'has-active' : ''}`}>
                          <button type="button" className="al-subgroup-head" onClick={() => toggleGroup(sgKey)}>
                            <span className="al-sub-icon">{sg.icon}</span>
                            <span className="al-subgroup-label">{sg.label}</span>
                            <span className="al-chev">{sgOpen ? '▾' : '▸'}</span>
                          </button>
                          {sgOpen && (
                            <div className="al-subgroup-items">
                              {sg.items.map(renderItem)}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}

          {/* Separador + accesos rápidos a vistas cliente para QA/testing.
              target=_blank — abren en pestaña nueva sin salir del admin. */}
          <div className="al-divider" aria-hidden></div>
          <div className="al-section-label">QA · Ver como…</div>
          <a
            href="/catalogo"
            target="_blank"
            rel="noopener noreferrer"
            className="al-link al-link-external"
            title="Abrir tienda con tu sesión admin en nueva pestaña"
          >
            <AlIco id="store" />
            <span>Tienda como cliente</span>
            <span className="al-ext-icon" aria-hidden>↗</span>
          </a>
          <a
            href="https://tienda.limpack.uy/tienda?beta=limpack-qa-mvd-7k9x"
            target="_blank"
            rel="noopener noreferrer"
            className="al-link al-link-external"
            title="Abre tienda.limpack.uy/tienda con bypass de BetaGate (?beta=ok) → ves la tienda real como visitante sin sesión"
          >
            <AlIco id="eye" />
            <span>Tienda sin usuario</span>
            <span className="al-ext-icon" aria-hidden>↗</span>
          </a>
          <a
            href="/cliente"
            target="_blank"
            rel="noopener noreferrer"
            className="al-link al-link-external"
            title="Abrir panel cliente en nueva pestaña"
          >
            <AlIco id="users" />
            <span>Panel cliente</span>
            <span className="al-ext-icon" aria-hidden>↗</span>
          </a>
        </nav>

        <div className="al-footer">
          <Link to="/admin/configuracion-ia" className="al-user">
            <div className="al-avatar">{initial}</div>
            <div className="al-user-info">
              <div className="al-user-name">{displayName}</div>
              <div className="al-user-email">{fullEmail}</div>
            </div>
          </Link>
          <button className="al-logout" onClick={handleLogout}>Salir</button>
        </div>
      </>
    )
  }

  return (
    <div className="al-shell">
      {/* Command palette Cmd+K */}
      {paletteOpen && (
        <CommandPalette onClose={() => setPaletteOpen(false)} onNavigate={(to) => { setPaletteOpen(false); navigate(to) }} />
      )}

      {/* Sidebar desktop */}
      <aside ref={sideRef} className="al-side">
        <NavBody />
      </aside>

      {/* Drawer mobile */}
      {drawerOpen && (
        <>
          <div className="al-overlay" onClick={() => setDrawerOpen(false)} />
          <aside className="al-side al-side-mobile">
            <NavBody />
          </aside>
        </>
      )}

      {/* Header mobile */}
      <header className="al-header-mobile">
        <button className="al-burger" onClick={() => setDrawerOpen(true)} aria-label="Menú">☰</button>
        <Link to="/admin" className="al-logo-mobile">Lim<span>pack</span><em>admin</em></Link>
        <button className="al-icon-btn" onClick={() => navigate('/admin/configuracion-ia')} aria-label="Cuenta">{initial}</button>
      </header>

      {/* Main */}
      <main className={"al-main" + (alUiModern ? " ui-modern" : "")}>
        {title && <div className="al-page-title">{title}</div>}
        {children}
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap');
        :root{
          --al-navy:#1A3461; --al-teal:#00A8B5; --al-sky:#5BBDD8; --al-paper:#F1F5F9;
          --al-surface:#fff; --al-surface-2:#F7F9FC; --al-surface-3:#EDF1F6;
          --al-ink:#0F1F3D; --al-ink-2:#3B4A66; --al-ink-3:#66748F; --al-ink-4:#98A3B8;
          --al-hair:rgba(26,52,97,.10); --al-hair-2:rgba(26,52,97,.06);
          --al-teal-ink:#0A7C86; --al-ok:#1A8F56;
          --al-line:#e2e8f0; --al-line2:#f1f5f9; --al-muted:#64748b; --al-muted2:#94a3b8; --al-card:#fff;
          --al-sb-bg:linear-gradient(180deg,#1E3B6E 0%,#1A3461 46%,#152C55 100%);
          --al-sb-glow:radial-gradient(90% 38% at 0% 0%, rgba(91,189,216,.24), transparent 70%);
          --al-sb-text:rgba(255,255,255,.74); --al-sb-muted:rgba(255,255,255,.40);
          --al-sb-hover:rgba(255,255,255,.07); --al-sb-line:rgba(255,255,255,.10);
          --al-shadow-card:0 1px 1px rgba(26,52,97,.03), 0 2px 6px -1px rgba(26,52,97,.05), 0 16px 36px -16px rgba(26,52,97,.12);
          --al-shadow-side:inset 0 1px 0 rgba(255,255,255,.07), 0 30px 56px -24px rgba(26,52,97,.5), 0 2px 8px rgba(26,52,97,.10);
          --al-gutter:14px; --al-sb-w:250px;
          --al-font:Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
          --al-display:Manrope, Inter, system-ui, sans-serif;
        }
        .al-shell{ min-height:100vh; background:var(--al-paper); color:var(--al-ink); font-family:var(--al-font); }

        /* Marca */
        .al-logo{ display:flex; align-items:center; gap:11px; padding:4px 6px 14px; text-decoration:none }
        .al-logo-mark{ width:34px;height:34px;border-radius:11px;flex:none; background:linear-gradient(135deg,var(--al-teal),var(--al-sky)); color:#fff; display:grid;place-items:center; font-family:var(--al-display); font-weight:800; font-size:17px; box-shadow:0 8px 16px -8px rgba(0,168,181,.9), inset 0 1px 0 rgba(255,255,255,.4) }
        .al-logo-tx{ display:flex; flex-direction:column; line-height:1.1 }
        .al-logo-name{ color:#fff; font-weight:700; font-size:15.5px; letter-spacing:-.01em; font-family:var(--al-display) }
        .al-logo-sub{ font-size:11.5px; color:var(--al-sb-muted); margin-top:3px }

        /* Reloj */
        .al-clock{ display:flex; align-items:center; justify-content:space-between; margin:0 6px 12px; padding:10px 12px; background:rgba(255,255,255,.05); border:1px solid var(--al-sb-line); border-radius:12px; color:rgba(255,255,255,.9) }
        .al-clock-time{ font-family:var(--al-display); font-size:18px; font-weight:700; color:#fff; font-variant-numeric:tabular-nums }
        .al-clock-date{ font-size:10.5px; color:var(--al-sb-muted); text-transform:capitalize; margin-top:1px }
        .al-clock-tz{ font-size:9px; font-weight:800; letter-spacing:.1em; color:var(--al-sky); text-transform:uppercase }

        /* Buscar (cmd) */
        .al-cmd-trigger{ display:flex; align-items:center; gap:8px; margin:0 6px 12px; padding:9px 12px; width:calc(100% - 12px); background:rgba(255,255,255,.06); border:1px solid var(--al-sb-line); border-radius:12px; color:rgba(255,255,255,.7); font-size:12.5px; font-family:inherit; cursor:pointer; transition:background .15s, border-color .15s }
        .al-cmd-trigger:hover{ background:rgba(0,168,181,.14); border-color:rgba(0,168,181,.4); color:#fff }
        .al-cmd-trigger kbd{ font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:10px; padding:1px 5px; background:rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.15); border-radius:5px; color:rgba(255,255,255,.7) }

        /* Conectado como */
        .al-me{ display:flex; align-items:center; gap:8px; margin:0 6px 14px; padding:9px 12px; background:rgba(0,168,181,.08); border:1px solid rgba(0,168,181,.2); border-radius:12px; color:rgba(255,255,255,.85); font-size:12px }
        .al-me-dot{ width:8px;height:8px;border-radius:50%; background:#3FBF7F; box-shadow:0 0 0 3px rgba(63,191,127,.25); flex-shrink:0 }
        .al-me-text strong{ font-weight:700; color:#fff }
        .al-me-readonly{ background:rgba(251,191,36,.18); color:#fbbf24; border:1px solid rgba(251,191,36,.4); font-size:9.5px; font-weight:700; padding:2px 7px; border-radius:999px; margin-left:auto; letter-spacing:.02em; cursor:help; white-space:nowrap }

        /* Presencia */
        .al-presence{ margin:2px 6px 8px; padding:11px 12px; background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); border-radius:12px }
        .al-presence-tag{ font-size:9px; font-weight:800; letter-spacing:.14em; color:rgba(255,255,255,.5); text-transform:uppercase; margin-bottom:8px }
        .al-presence-row{ display:flex; align-items:center; gap:8px; font-size:12px; color:rgba(255,255,255,.85); padding:4px 0 }
        .al-presence-dot{ width:7px;height:7px;border-radius:50%; background:#3FBF7F; flex-shrink:0 }
        .al-presence-name{ font-weight:700; color:#fff }
        .al-presence-where{ font-size:11px; color:rgba(255,255,255,.55); margin-left:auto; max-width:110px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap }

        /* Sidebar flotante */
        .al-side{
          display:none; position:fixed; top:var(--al-gutter); left:var(--al-gutter); bottom:var(--al-gutter);
          width:var(--al-sb-w); border-radius:22px; background:var(--al-sb-glow), var(--al-sb-bg);
          box-shadow:var(--al-shadow-side); color:var(--al-sb-text); flex-direction:column; z-index:50;
          padding:16px 12px 12px; overflow-y:auto; overflow-x:hidden;
          -webkit-overflow-scrolling:touch; touch-action:pan-y; overscroll-behavior:contain;
        }
        .al-side::-webkit-scrollbar{ width:7px }
        .al-side::-webkit-scrollbar-thumb{ background:rgba(255,255,255,.16); border-radius:8px }
        .al-nav{ display:flex; flex-direction:column; gap:2px; flex:1 }

        /* Íconos SVG */
        .al-ic{ width:16px; height:16px; flex:none }

        /* Link top-level y group-head */
        .al-link, .al-group-head{
          display:flex; align-items:center; gap:10px; width:100%; height:36px; padding:0 10px;
          border-radius:10px; color:var(--al-sb-text); font-size:14.5px; font-weight:500;
          text-decoration:none; background:none; border:0; cursor:pointer; text-align:left; font-family:inherit;
          transition:background .15s, color .15s;
        }
        .al-group-head{ text-transform:none; letter-spacing:0 }
        @media (hover:hover) and (pointer:fine){ .al-link:hover, .al-group-head:hover{ background:var(--al-sb-hover); color:#fff } }
        .al-link .al-ic, .al-group-head .al-ic{ opacity:.78; transition:opacity .15s }
        .al-link:hover .al-ic, .al-group.open > .al-group-head .al-ic{ opacity:1 }
        .al-link.active{ color:#fff; background:linear-gradient(180deg,rgba(255,255,255,.16),rgba(255,255,255,.09)); box-shadow:inset 0 0 0 1px rgba(255,255,255,.12), 0 10px 18px -12px rgba(0,0,0,.7) }
        .al-link.active .al-ic{ opacity:1 }

        /* Pip de estado */
        .al-status-pip{ margin-left:auto; width:9px; height:9px; border-radius:50%; flex-shrink:0 }
        .al-status-pip-warn{ animation:alPipPulseWarn 2s infinite }
        .al-status-pip-error{ animation:alPipPulseError 1.2s infinite }
        @keyframes alPipPulseWarn{ 0%,100%{ box-shadow:0 0 0 0 rgba(245,158,11,.55) } 50%{ box-shadow:0 0 0 5px rgba(245,158,11,0) } }
        @keyframes alPipPulseError{ 0%,100%{ box-shadow:0 0 0 0 rgba(220,38,38,.7); opacity:1 } 50%{ box-shadow:0 0 0 6px rgba(220,38,38,0); opacity:.55 } }

        /* Separador + QA */
        .al-divider{ height:1px; background:rgba(255,255,255,.08); margin:14px 6px 6px }
        .al-section-label{ font-size:9px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:rgba(255,255,255,.35); padding:6px 12px 4px }

        /* Eyebrows de canal (limpio, sin bg ni icono) */
        .al-menu-section-header{ display:flex; align-items:center; gap:8px; margin:22px 12px 8px; padding:0; border:0; background:none; font-size:11.5px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; color:var(--al-sb-muted) }
        .al-menu-section-icon{ display:none }
        .al-menu-section-text{ flex:1 }
        .al-section-limpack, .al-section-ml, .al-section-shared{ color:var(--al-sb-muted); border:0; background:none }

        /* Links externos (QA) */
        .al-link-external{ color:rgba(255,255,255,.55); font-weight:500 }
        .al-link-external:hover{ color:rgba(255,255,255,.9); background:rgba(91,189,216,.10) }
        .al-ext-icon{ margin-left:auto; font-size:12px; opacity:.6 }

        /* Grupos */
        .al-group{ display:flex; flex-direction:column }
        .al-group.has-active > .al-group-head{ color:rgba(255,255,255,.92) }
        .al-group-label{ flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis }
        .al-chev{ font-size:10px; opacity:.45; margin-left:2px }
        .al-group-items{ display:flex; flex-direction:column; gap:2px; padding:3px 0 8px 12px; margin-left:6px; border-left:1px solid var(--al-sb-line) }

        /* Sublinks (punto en vez de icono, pill de activo) */
        .al-sublink{ display:flex; align-items:center; gap:10px; height:32px; padding:0 10px; border-radius:10px; color:rgba(255,255,255,.66); font-size:14px; font-weight:400; text-decoration:none; transition:background .15s, color .15s }
        .al-sublink::before{ content:""; width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,.18); flex:none; transition:background .15s, box-shadow .15s }
        .al-sublink:hover{ color:#fff }
        .al-sublink:hover::before{ background:rgba(255,255,255,.5) }
        .al-sublink.active{ color:#fff; font-weight:600; background:linear-gradient(180deg,rgba(255,255,255,.16),rgba(255,255,255,.09)); box-shadow:inset 0 0 0 1px rgba(255,255,255,.12), 0 10px 18px -12px rgba(0,0,0,.7) }
        .al-sublink.active::before{ background:var(--al-teal); box-shadow:0 0 0 3px rgba(0,168,181,.3) }
        .al-sub-icon{ display:none }

        /* Badges (sky) */
        .al-badge{ margin-left:auto; background:var(--al-sky); color:var(--al-navy); font-size:10.5px; font-weight:700; padding:0 6px; min-width:19px; height:19px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; line-height:1; font-variant-numeric:tabular-nums }
        .al-badge-group{ margin-left:auto; margin-right:2px }

        /* Subgrupos (Finanzas) */
        .al-subgroup{ display:flex; flex-direction:column; margin-top:2px }
        .al-subgroup-head{ display:flex; align-items:center; gap:8px; height:30px; padding:0 10px; border-radius:9px; background:none; border:0; cursor:pointer; color:rgba(255,255,255,.6); font-size:13px; font-weight:600; font-family:inherit; text-align:left; transition:color .15s, background .15s }
        .al-subgroup-head:hover{ color:#fff; background:rgba(255,255,255,.05) }
        .al-subgroup.has-active > .al-subgroup-head{ color:rgba(255,255,255,.9) }
        .al-subgroup-label{ flex:1 }
        .al-subgroup-items{ display:flex; flex-direction:column; gap:2px; padding:2px 0 4px 6px; margin-left:6px; border-left:1px solid rgba(255,255,255,.06) }
        .al-subgroup-items .al-sublink{ font-size:13.5px; height:30px }

        /* Footer */
        .al-footer{ display:flex; flex-direction:column; gap:8px; margin-top:14px; padding-top:14px; border-top:1px solid var(--al-sb-line) }
        .al-user{ display:flex; align-items:center; gap:10px; padding:9px; border-radius:12px; background:rgba(255,255,255,.05); text-decoration:none }
        .al-user:hover{ background:rgba(255,255,255,.09) }
        .al-avatar{ width:32px; height:32px; border-radius:50%; background:linear-gradient(135deg,var(--al-teal),var(--al-sky)); display:grid; place-items:center; font-weight:800; color:#fff; font-size:13px; font-family:var(--al-display); flex-shrink:0 }
        .al-user-info{ flex:1; min-width:0 }
        .al-user-name{ font-size:12.5px; font-weight:700; color:#fff }
        .al-user-email{ font-size:10px; color:rgba(255,255,255,.5); white-space:nowrap; overflow:hidden; text-overflow:ellipsis }
        .al-logout{ background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.1); color:rgba(255,255,255,.85); border-radius:10px; padding:8px; font-size:11.5px; font-weight:700; cursor:pointer; font-family:inherit }
        .al-logout:hover{ background:rgba(255,255,255,.1) }

        /* Drawer mobile + overlay */
        .al-overlay{ position:fixed; inset:0; background:rgba(15,31,61,.5); z-index:49; backdrop-filter:blur(3px) }
        .al-side-mobile{ display:flex; top:0; left:0; bottom:0; border-radius:0 22px 22px 0; width:min(300px,86vw) }

        /* Header mobile */
        .al-header-mobile{ position:sticky; top:0; z-index:40; background:rgba(241,245,249,.82); -webkit-backdrop-filter:blur(12px); backdrop-filter:blur(12px); border-bottom:1px solid var(--al-hair-2); padding:calc(10px + env(safe-area-inset-top,0px)) 14px 10px; display:flex; align-items:center; justify-content:space-between; gap:10px }
        .al-burger{ background:var(--al-surface); border:0; box-shadow:inset 0 0 0 1px var(--al-hair), var(--al-shadow-card); border-radius:12px; width:40px; height:40px; cursor:pointer; font-size:18px; color:var(--al-ink-2); flex-shrink:0 }
        .al-logo-mobile{ color:var(--al-navy); padding:0; font-size:18px; font-weight:900; letter-spacing:-.02em; white-space:nowrap; text-decoration:none; flex:1; text-align:center; display:inline-flex; align-items:baseline; justify-content:center; font-family:var(--al-display) }
        .al-logo-mobile span{ color:var(--al-teal) }
        .al-logo-mobile em{ color:var(--al-muted2); font-size:11px; margin-left:6px; font-style:normal; text-transform:uppercase; letter-spacing:.1em }
        .al-icon-btn{ width:38px; height:38px; border-radius:50%; background:linear-gradient(135deg,var(--al-teal),var(--al-sky)); color:#fff; border:0; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:800; font-family:var(--al-display) }

        /* Main */
        .al-main{ padding:18px 16px 60px; min-height:100vh }
        .al-page-title{ font-family:var(--al-display); font-size:24px; font-weight:800; letter-spacing:-.03em; margin-bottom:14px; color:var(--al-ink) }

        @media (min-width:960px){
          .al-side{ display:flex }
          .al-header-mobile{ display:none }
          .al-main{ margin-left:calc(var(--al-sb-w) + var(--al-gutter) * 2); padding:26px 32px 64px }
          .al-page-title{ font-size:28px; margin-bottom:18px }
        }

        /* Escalado responsive por tamaño de monitor (2026-09-02).
           En pantallas grandes agranda todo el panel de forma pareja (texto, UI,
           sidebar y contenido) y de paso reduce el aire desperdiciado al costado.
           zoom = magnificacion real: respeta position:fixed (a diferencia de transform:scale).
           Laptops y pantallas chicas quedan intactas. Reversible: borrar este bloque. */
        @media (min-width:1500px){ .al-shell{ zoom:1.06 } }
        @media (min-width:1800px){ .al-shell{ zoom:1.12 } }
        @media (min-width:2200px){ .al-shell{ zoom:1.18 } }
        @media (min-width:2600px){ .al-shell{ zoom:1.25 } }

        @media (prefers-reduced-motion:reduce){ .al-shell *{ transition:none !important; animation:none !important } }

        /* Compat legacy */
        .al-main .page{ padding:0 }
        .al-main .page > h1:first-child{ margin-top:0 }
        .al-main .lead{ color:var(--al-muted); margin-top:-6px }

      `}</style>
    </div>
  )
}

/* ─────────── Command Palette (Cmd+K) ─────────── */

// Herramientas del "modo avanzado" de Comunicación: salieron del menú visible
// (26-jul-2026) pero sus rutas siguen vivas — Cmd+K las tiene que encontrar igual.
const EXTRAS_PALETTE = [
  { to: '/admin/marketing',             label: 'Taller de piezas (Hub Marketing)', icon: '🏭' },
  { to: '/admin/marketing-agent',       label: 'Chat con el Gerente Marketing',    icon: '🎩' },
  { to: '/admin/calendario-marketing',  label: 'Calendario a mano (avanzado)',     icon: '🗓️' },
  { to: '/admin/marketing/volante',     label: 'Builder de volantes (edificios)',  icon: '📄' },
  { to: '/admin/marketing/lotes',       label: 'Lotes & tracking QR',              icon: '🔗' },
  { to: '/admin/marketing/meta-ads',    label: 'Meta Ads (pauta)',                 icon: '💰' },
]

// Aplasta el MENU en una lista plana { to, label, icon, ruta }
// donde "ruta" es el contexto/grupo para mostrar al usuario ("Catálogo › Stock productos").
function flatMenu() {
  const out = EXTRAS_PALETTE.map(e => ({
    ...e, ruta: 'Comunicación › Modo avanzado', _searchKey: `Comunicación marketing avanzado ${e.label}`,
  }))
  for (const m of MENU) {
    if (m.type === 'link') {
      out.push({ to: m.to, label: m.label, icon: m.icon, ruta: m.label, _searchKey: m.label })
    } else if (m.type === 'group') {
      const padre = m.label
      if (m.items) {
        for (const i of m.items) {
          out.push({ to: i.to, label: i.label, icon: i.icon, ruta: `${padre} › ${i.label}`,
                     _searchKey: `${padre} ${i.label}` })
        }
      }
      if (m.subgroups) {
        for (const sg of m.subgroups) {
          for (const i of sg.items) {
            out.push({ to: i.to, label: i.label, icon: i.icon,
                       ruta: `${padre} › ${sg.label} › ${i.label}`,
                       _searchKey: `${padre} ${sg.label} ${i.label}` })
          }
        }
      }
    }
  }
  return out
}

// Reloj Montevideo — actualiza cada segundo, timezone America/Montevideo
function RelojMontevideo() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  const hora = now.toLocaleTimeString('es-UY', { timeZone: 'America/Montevideo', hour: '2-digit', minute: '2-digit', hour12: false })
  const fechaLarga = now.toLocaleDateString('es-UY', { timeZone: 'America/Montevideo', weekday: 'long', day: 'numeric', month: 'short' })
  return (
    <div className="al-clock" title="Hora Montevideo">
      <div>
        <div className="al-clock-time">{hora}</div>
        <div className="al-clock-date">{fechaLarga}</div>
      </div>
      <div className="al-clock-tz">MVD</div>
    </div>
  )
}

// Match simple case/accent-insensitive
function normalizar(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

function CommandPalette({ onClose, onNavigate }) {
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const items = flatMenu()

  useEffect(() => { inputRef.current?.focus() }, [])

  const q = normalizar(query.trim())
  const filtrados = q === ''
    ? items.slice(0, 12)
    : items
        .map(it => ({ it, score: scoreMatch(normalizar(it._searchKey), q) }))
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 20)
        .map(x => x.it)

  useEffect(() => { setIndex(0) }, [query])

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndex(i => Math.min(i + 1, filtrados.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIndex(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      const it = filtrados[index]
      if (it) onNavigate(it.to)
    }
  }

  // Auto-scroll del item activo a vista
  useEffect(() => {
    const el = listRef.current?.querySelector('.al-cmd-item.is-active')
    el?.scrollIntoView({ block: 'nearest' })
  }, [index, filtrados.length])

  return (
    <div className="al-cmd-overlay" onClick={onClose}>
      <div className="al-cmd" onClick={e => e.stopPropagation()}>
        <div className="al-cmd-input-wrap">
          <span className="al-cmd-icon">🔍</span>
          <input
            ref={inputRef}
            className="al-cmd-input"
            placeholder="Buscar pantalla… (clientes, cobros, stock, etc.)"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="al-cmd-kbd">esc</kbd>
        </div>
        <div className="al-cmd-list" ref={listRef}>
          {filtrados.length === 0 ? (
            <div className="al-cmd-empty">Sin coincidencias para "{query}"</div>
          ) : filtrados.map((it, i) => (
            <button
              key={it.to}
              className={`al-cmd-item ${i === index ? 'is-active' : ''}`}
              onMouseEnter={() => setIndex(i)}
              onClick={() => onNavigate(it.to)}
            >
              <span className="al-cmd-item-icon">{it.icon}</span>
              <div className="al-cmd-item-body">
                <div className="al-cmd-item-label">{it.label}</div>
                <div className="al-cmd-item-ruta">{it.ruta}</div>
              </div>
              <span className="al-cmd-item-route">{it.to}</span>
            </button>
          ))}
        </div>
        <div className="al-cmd-footer">
          <kbd>↑↓</kbd> navegar <kbd>↵</kbd> ir <kbd>esc</kbd> cerrar
          <span style={{ marginLeft: 'auto' }}>⌘K para abrir/cerrar</span>
        </div>

        <style>{`
          .al-cmd-overlay {
            position: fixed; inset: 0; z-index: 9999;
            background: rgba(15,27,54,0.55);
            backdrop-filter: blur(3px);
            display: flex; align-items: flex-start; justify-content: center;
            padding-top: 10vh;
            animation: alCmdFade 0.15s ease-out;
          }
          @keyframes alCmdFade { from { opacity: 0 } to { opacity: 1 } }
          .al-cmd {
            background: #fff; border-radius: 14px;
            box-shadow: 0 20px 60px rgba(15,27,54,0.4);
            width: 92%; max-width: 580px;
            max-height: 70vh; display: flex; flex-direction: column;
            animation: alCmdSlide 0.18s ease-out;
            overflow: hidden;
            font-family: inherit;
          }
          @keyframes alCmdSlide {
            from { transform: translateY(-10px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          .al-cmd-input-wrap {
            display: flex; align-items: center; gap: 10px;
            padding: 14px 16px; border-bottom: 1px solid #E2E8F0;
          }
          .al-cmd-icon { font-size: 16px; opacity: 0.6; }
          .al-cmd-input {
            flex: 1; border: 0; outline: none;
            font-size: 15px; font-family: inherit;
            background: transparent; color: #1A3461;
          }
          .al-cmd-input::placeholder { color: #94A3B8; }
          .al-cmd-kbd, .al-cmd-footer kbd {
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            font-size: 10px; padding: 2px 6px;
            background: #F1F5F9; border: 1px solid #E2E8F0;
            border-bottom-width: 2px;
            border-radius: 4px; color: #475569;
          }
          .al-cmd-list {
            flex: 1; overflow-y: auto;
            padding: 6px 0;
            min-height: 60px;
          }
          .al-cmd-empty {
            padding: 24px; text-align: center;
            color: #94A3B8; font-size: 13px;
          }
          .al-cmd-item {
            display: flex; align-items: center; gap: 10px;
            padding: 9px 16px; width: 100%;
            background: transparent; border: 0; cursor: pointer;
            font-family: inherit; text-align: left;
            transition: background 0.08s;
          }
          .al-cmd-item.is-active {
            background: #F1F5F9;
          }
          .al-cmd-item-icon { font-size: 18px; width: 22px; text-align: center; }
          .al-cmd-item-body { flex: 1; min-width: 0; }
          .al-cmd-item-label {
            font-size: 14px; font-weight: 600; color: #1A3461;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          }
          .al-cmd-item-ruta {
            font-size: 11px; color: #94A3B8;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          }
          .al-cmd-item.is-active .al-cmd-item-ruta { color: #64748B; }
          .al-cmd-item-route {
            font-size: 10px; color: #CBD5E1;
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            opacity: 0; transition: opacity 0.12s;
          }
          .al-cmd-item.is-active .al-cmd-item-route { opacity: 1; }
          .al-cmd-footer {
            padding: 9px 16px; border-top: 1px solid #E2E8F0;
            font-size: 11px; color: #94A3B8;
            display: flex; gap: 10px; align-items: center;
          }
        `}</style>
      </div>
    </div>
  )
}

// Score: items que arrancan con la query rankean más alto que los que la contienen
function scoreMatch(haystack, q) {
  if (haystack.includes(q)) {
    if (haystack.startsWith(q)) return 100
    // Bonus si match arranca palabra
    const words = haystack.split(/\s+/)
    if (words.some(w => w.startsWith(q))) return 80
    return 50
  }
  // Fuzzy MUY soft: cada caracter de q debe aparecer en orden
  let i = 0
  for (const ch of haystack) {
    if (ch === q[i]) i++
    if (i === q.length) return 20
  }
  return 0
}

/* El componente LivePresenceCounter se movió a ./LivePresenceCounter.jsx
   y ahora se renderiza dentro de /admin/infraestructura como tarjeta. */
